/* /admin/settings — plan prices and receiving bank accounts (prototype billing.js pgAdminSettings). */
import { useEffect, useId, useState } from "react";
import { ApiError } from "../../data/provider";
import { useUpdateBankAccount, useUpdatePlanPrices } from "../../data/hooks";
import type { BankAccount } from "../../data/types";
import { useAdminSettingsData, useStoreAdminSettings } from "./useAdminSettingsData";
import { AI_PACK_PRICE, normIban, validIban } from "../../domain/compliance";
import ChangePasswordCard from "../account/ChangePasswordCard";
import { useI18n } from "../../i18n";
import { Affix, Alert, Button, Card, Field, Grid, Input, Kv, Loaded, PageHeader, Toggle, errMsg, useToast } from "../../ui";
import { PLANS, TRIAL_DAYS } from "../public/components/pricingData";

/** Format an IBAN in groups of 4 (display + save). */
export const fmtIban = (s: string) => normIban(s).replace(/(.{4})/g, "$1 ").trim();

function BankCard({ b, i }: { b: BankAccount; i: number }) {
  const { L } = useI18n();
  const toast = useToast();
  const upd = useUpdateBankAccount();
  const store = useStoreAdminSettings();
  const id = useId();
  const [edit, setEdit] = useState(false);
  const [iban, setIban] = useState(b.iban);
  const [err, setErr] = useState<string | undefined>();
  useEffect(() => { if (!edit) setIban(b.iban); }, [b.iban, edit]);
  const save = async () => {
    if (!validIban(iban)) { setErr(L("IBAN must be SA followed by 22 digits", "الآيبان يجب أن يبدأ بـ SA متبوعًا بـ 22 رقمًا")); return; }
    try { store({ bankAccounts: await upd.mutateAsync({ index: i, acct: { iban: fmtIban(iban) } }) }); toast(L("Bank account updated", "تم تحديث الحساب البنكي")); setEdit(false); setErr(undefined); }
    catch (e) { if (e instanceof ApiError && e.fields?.iban) setErr(e.fields.iban); else toast(errMsg(e), "bad"); }
  };
  const toggle = async (active: boolean) => {
    try { store({ bankAccounts: await upd.mutateAsync({ index: i, acct: { active } }) }); toast(active ? L("Shown to customers", "يظهر للعملاء") : L("Hidden from customers", "مخفي عن العملاء")); }
    catch (e) { toast(errMsg(e), "bad"); }
  };
  return (
    <div className="bankcard" data-testid={"bank-" + i}>
      <div className="row between">
        <b>{L(b.bank, b.bankAr)}</b>
        <span className="row nw sm"><Toggle checked={b.active} onChange={toggle} label={L("Shown to customers", "يظهر للعملاء") + " · " + b.bank} testId={"bank-active-" + i} />{L("Shown to customers", "يظهر للعملاء")}</span>
      </div>
      {edit ? (
        <div className="stack" style={{ marginTop: 10 }}>
          <Field label="IBAN" htmlFor={id} err={err}>
            <Input id={id} dir="ltr" className="num" value={iban} bad={!!err} onChange={(e) => { setIban(e.target.value); setErr(undefined); }} data-testid={"bank-iban-" + i} />
          </Field>
          <div className="row"><Button variant="sm" onClick={() => { setEdit(false); setErr(undefined); }}>{L("Cancel", "إلغاء")}</Button><Button variant="sm pri" loading={upd.isPending} onClick={save} data-testid={"bank-save-" + i}>{L("Save", "حفظ")}</Button></div>
        </div>
      ) : (
        <>
          <Kv items={[[L("Beneficiary", "المستفيد"), L(b.name, b.nameAr)], ["IBAN", <span key="i" className="num" dir="ltr" data-testid={"bank-iban-view-" + i}>{b.iban}</span>], [L("Account no.", "رقم الحساب"), <span key="a" className="num">{b.acct}</span>]]} />
          <Button variant="ghost sm" icon="edit" onClick={() => setEdit(true)} data-testid={"bank-edit-" + i}>{L("Edit IBAN", "تعديل الآيبان")}</Button>
        </>
      )}
    </div>
  );
}

function PricesCard({ prices }: { prices: Record<string, number> }) {
  const { L, t } = useI18n();
  const toast = useToast();
  const upd = useUpdatePlanPrices();
  const store = useStoreAdminSettings();
  const [f, setF] = useState<Record<string, string>>(() => Object.fromEntries(PLANS.map((p) => [p, String(prices[p] ?? "")])));
  const [fe, setFe] = useState<Record<string, string>>({});
  const dirty = PLANS.some((p) => Number(f[p]) !== prices[p]);
  const save = async () => {
    const e: Record<string, string> = {};
    for (const p of PLANS) { const n = Number(f[p]); if (f[p].trim() === "" || !Number.isFinite(n) || n < 1 || n > 1000) e[p] = L("Enter a price between 1 and 1,000", "أدخل سعرًا بين 1 و1,000"); }
    setFe(e);
    if (Object.keys(e).length) return;
    try { store({ planPrices: await upd.mutateAsync(Object.fromEntries(PLANS.map((p) => [p, Number(f[p])]))) }); toast(L("Plan prices saved", "تم حفظ أسعار الباقات")); }
    catch (err) { if (err instanceof ApiError && err.fields) setFe(err.fields); toast(errMsg(err), "bad"); }
  };
  return (
    <Card title={L("Plans & trial", "الباقات والتجربة")} testId="prices-card">
      <div className="stack">
        {PLANS.map((p) => (
          <Field key={p} label={p} err={fe[p]} htmlFor={"price-" + p}>
            <Affix pre={L("SAR", "ر.س")} post={L("/ employee / month", "/ موظف / شهر")}>
              <Input id={"price-" + p} className="num" type="number" min={1} max={1000} value={f[p]} bad={!!fe[p]} onChange={(e) => { setF({ ...f, [p]: e.target.value }); setFe({ ...fe, [p]: "" }); }} data-testid={"price-" + p} />
            </Affix>
          </Field>
        ))}
        <Kv items={[[L("Free trial length", "مدة التجربة المجانية"), TRIAL_DAYS + " " + t("days")], [L("Annual discount", "خصم الاشتراك السنوي"), "20%"], [L("AI credits pack", "باقة رصيد الذكاء"), L(`SAR ${AI_PACK_PRICE} / 1,000 credits / month`, `${AI_PACK_PRICE} ر.س / 1,000 رصيد / شهر`)]]} />
        <p className="xs muted" style={{ margin: 0 }}>{L("New prices apply to the next invoice of every customer. Trials can be extended per company.", "تطبق الأسعار الجديدة على الفاتورة القادمة لكل عميل. يمكن تمديد التجربة لكل منشأة.")}</p>
        <div className="row"><Button variant="pri" icon="check" disabled={!dirty} loading={upd.isPending} onClick={save} data-testid="prices-save">{L("Save prices", "حفظ الأسعار")}</Button></div>
      </div>
    </Card>
  );
}

export default function AdminSettings() {
  const { L } = useI18n();
  const q = useAdminSettingsData();
  return (
    <div data-testid="admin-settings">
      <PageHeader title={L("Plans & bank accounts", "الباقات والحسابات البنكية")} sub={L("What customers see on the billing page", "ما يراه العملاء في صفحة الفوترة")} />
      <Loaded q={q}>{(d) => (
        <Grid cols="2">
          <Card title={L("Receiving bank accounts", "الحسابات البنكية للاستلام")} testId="banks-card">
            <div className="stack">
              {d.bankAccounts.map((b, i) => <BankCard key={i} b={b} i={i} />)}
              {d.bankAccounts.every((b) => !b.active) && <Alert tone="warn" title={L("No account is shown to customers", "لا يوجد حساب ظاهر للعملاء")} desc={L("Customers can't pay until at least one account is active.", "لا يمكن للعملاء الدفع حتى يتم تفعيل حساب واحد على الأقل.")} />}
            </div>
          </Card>
          <PricesCard key={JSON.stringify(d.planPrices)} prices={d.planPrices} />
          <ChangePasswordCard />
        </Grid>
      )}</Loaded>
    </div>
  );
}
